import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, CircularProgress, Table, TableBody, TableContainer } from '@mui/material'
import {
  resetExpandedRowsTableStore,
  setExpandedRowsTableStore,
  useTableExpandedRowsStore
} from '@/stores/useTableExpandedRowsStore'
import { setTablePageSize, useUiStore } from '@/stores/useUiStore'

import { Head } from './components/Head'
import { LoadingSkeleton } from './components/LoadingSkeleton'
import { NoDataPlaceholder } from './components/NoDataPlaceholder'
import { Pagination } from './components/Pagination'
import { Row } from './components/Row'
import { useColumnVisibility } from './hooks/useColumnVisibility'
import { getDataWithId } from './utils/getDataWithId'
import { getPaginatedData } from './utils/getPaginatedData'
import { getSortedData } from './utils/getSortedData'
import { SORT_ORDER } from './TableBase.constants'
import type {
  Pagination as PaginationType,
  Sort,
  SortOrder,
  TableBaseProps
} from './TableBase.types'

export const TableBase = <Data extends Record<string, unknown>, SortAttributes extends string>({
  data,
  dense,
  columns,
  loading,
  fetching,
  rowActions,
  noDataMessage = 'No data available',
  rowIdProperty,
  onRowClick,
  isRowClickable,

  exclusiveRowDetails,
  renderRowDetails,

  withCheckboxes,
  selectedRows = {},
  onRowCheck,
  isRowCheckable,

  sort,
  pagination,
  defaultSort,
  defaultPagination,
  clientSideSortAndPagination,
  disableColumnVisibility,
  onSortChange,
  onPaginationChange,

  headerRowSx,
  contentRowSx,
  tableId,
  persistExpandedRows = false
}: TableBaseProps<Data, SortAttributes>) => {
  const { columnVisibility, toggleColumnVisibility, checkColumnVisibility } =
    useColumnVisibility(columns)

  const persistedExpandedRows = useTableExpandedRowsStore((state) => state[tableId || '']) as
    | Array<Data & { __id: string }>
    | undefined

  const persistedExpandedRowsRecord = useMemo(() => {
    if (!persistedExpandedRows) return {}

    return persistedExpandedRows.reduce<Record<string, boolean>>((acc, { __id }) => {
      if (!__id) {
        return acc
      }

      acc[__id] = true
      return acc
    }, {})
  }, [persistedExpandedRows])

  const [internalSort, setInternalSort] = useState<Sort<SortAttributes> | undefined>(
    defaultSort
      ? {
          attribute: defaultSort.attribute as SortAttributes,
          order: defaultSort.order ?? SORT_ORDER.ASC
        }
      : undefined
  )

  const [internalPagination, setInternalPagination] = useState<PaginationType>({
    page: defaultPagination?.page ?? 0,
    pageSize: defaultPagination?.pageSize ?? useUiStore.getState().tablePageSize,
    total: 0
  })

  const [internalExpandedRows, setInternalExpandedRows] = useState<Record<string, boolean>>({})

  const expandedRows = useMemo(() => {
    if (!persistedExpandedRowsRecord || Object.keys(persistedExpandedRowsRecord).length === 0) {
      return internalExpandedRows
    }

    return {
      ...internalExpandedRows,
      ...persistedExpandedRowsRecord
    }
  }, [internalExpandedRows, persistedExpandedRowsRecord])

  useEffect(() => {
    if (!tableId) return
    setExpandedRowsTableStore(tableId, Object.keys(internalExpandedRows))
  }, [internalExpandedRows, tableId])

  useEffect(() => {
    if (!persistExpandedRows && tableId) {
      return () => {
        resetExpandedRowsTableStore(tableId)
      }
    }
  }, [persistExpandedRows, tableId])

  const sortToUse = sort ?? internalSort
  const paginationToUse = pagination ?? internalPagination

  useEffect(() => {
    if (clientSideSortAndPagination) {
      setInternalPagination((prev) => ({ ...prev, total: data?.length || 0 }))
    }
  }, [data, clientSideSortAndPagination])

  const dataWithInternalId = useMemo(
    () => getDataWithId(data || [], rowIdProperty),
    [data, rowIdProperty]
  )

  const sortedData = useMemo(() => {
    if (!clientSideSortAndPagination || !sortToUse?.order) {
      return dataWithInternalId
    }

    return getSortedData({
      data: dataWithInternalId,
      columns,
      sortAttribute: sortToUse.attribute,
      sortOrder: sortToUse.order
    })
  }, [dataWithInternalId, columns, sortToUse, clientSideSortAndPagination])

  const paginatedData = useMemo(() => {
    if (!clientSideSortAndPagination) {
      return sortedData
    }

    return getPaginatedData({
      data: sortedData,
      page: paginationToUse.page,
      pageSize: paginationToUse.pageSize
    })
  }, [sortedData, paginationToUse.page, paginationToUse.pageSize, clientSideSortAndPagination])

  const displayData = paginatedData || []

  const getSelectedAllRowsData = useCallback(
    (selectionState: Record<string, boolean>) => {
      return dataWithInternalId.filter((row) => selectionState[row.__id])
    },
    [dataWithInternalId]
  )

  const handleSortChange = useCallback(
    (newSortAttribute: SortAttributes) => {
      let newSortOrder: SortOrder | undefined = SORT_ORDER.ASC

      if (sortToUse?.attribute === newSortAttribute) {
        if (sortToUse.order === SORT_ORDER.ASC) {
          newSortOrder = SORT_ORDER.DESC
        } else if (sortToUse.order === SORT_ORDER.DESC) {
          newSortOrder = undefined
        }
      }

      const newSort: Sort<SortAttributes> = {
        attribute: newSortAttribute,
        order: newSortOrder
      }

      if (clientSideSortAndPagination) {
        setInternalSort(newSortOrder ? newSort : undefined)
        setInternalPagination((prev) => ({ ...prev, page: 0 }))
      }

      onSortChange?.(newSort.order ? newSort : null)
      setInternalExpandedRows({})
    },
    [sortToUse, clientSideSortAndPagination, onSortChange]
  )

  const handleRowCheck = useCallback(
    (id: string, checked: boolean) => {
      if (!withCheckboxes || !onRowCheck) return

      const newSelectedRows = {
        ...selectedRows,
        [id]: checked
      }

      onRowCheck(getSelectedAllRowsData(newSelectedRows))
    },
    [selectedRows, withCheckboxes, onRowCheck, getSelectedAllRowsData]
  )

  const handleCheckAll = useCallback(
    (checked: boolean) => {
      if (!withCheckboxes || !onRowCheck) return

      const newSelectedRows = { ...selectedRows }

      displayData.forEach((row) => {
        const rowIsSelectable = isRowCheckable ? isRowCheckable(row) : true
        if (rowIsSelectable) {
          newSelectedRows[row.__id] = checked
        }
      })

      onRowCheck(getSelectedAllRowsData(newSelectedRows))
    },
    [displayData, selectedRows, withCheckboxes, onRowCheck, isRowCheckable, getSelectedAllRowsData]
  )

  const handlePaginationChange = useCallback(
    (newPage: number, newPageSize: number) => {
      if (clientSideSortAndPagination) {
        setInternalPagination((prev) => ({
          ...prev,
          page: internalPagination.pageSize !== newPageSize ? 0 : newPage,
          pageSize: newPageSize
        }))
      }

      setTablePageSize(newPageSize)
      onPaginationChange?.({
        page: pagination.pageSize !== newPageSize ? 0 : newPage,
        pageSize: newPageSize
      })
      setInternalExpandedRows({})
    },
    [
      clientSideSortAndPagination,
      onPaginationChange,
      internalPagination.pageSize,
      setTablePageSize,
      pagination?.pageSize
    ]
  )

  const handleToggleRowDetails = (rowId: string) => {
    setInternalExpandedRows((prevExpandedRows) =>
      exclusiveRowDetails
        ? { [rowId]: !prevExpandedRows[rowId] }
        : { ...prevExpandedRows, [rowId]: !prevExpandedRows[rowId] }
    )
  }

  const isAnyRowSelected = Object.values(selectedRows).some(Boolean)
  const areAllRowsSelected =
    displayData.length > 0 &&
    displayData.every((row) => (isRowCheckable?.(row) ?? true) && selectedRows[row.__id])
  const columnCount =
    (withCheckboxes ? 1 : 0) +
    columnVisibility.filter(({ visible }) => visible).length +
    (rowActions ? 2 : 0)
  const shouldDisplayPagination = pagination !== undefined || clientSideSortAndPagination
  const shouldDisplayNoData = !loading && displayData.length === 0
  const shouldDisplayData = !loading && displayData.length > 0

  return (
    <Box width="100%" position="relative">
      {fetching && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor={({ palette: { mode } }) =>
            mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.2)'
          }
          zIndex={1}
        >
          <CircularProgress />
        </Box>
      )}

      <TableContainer sx={{ flex: '1 1 0' }}>
        <Table size={dense ? 'small' : 'medium'}>
          <Head
            rowSx={headerRowSx}
            columns={columns}
            sortOrder={sortToUse?.order || undefined}
            rowActions={rowActions}
            sortAttribute={sortToUse?.attribute}
            withCheckboxes={withCheckboxes}
            handleCheckAll={handleCheckAll}
            isAnyRowChecked={isAnyRowSelected}
            columnVisibility={!disableColumnVisibility && columnVisibility}
            handleSortChange={handleSortChange}
            areAllRowsChecked={areAllRowsSelected}
            checkColumnVisibility={checkColumnVisibility}
            toggleColumnVisibility={toggleColumnVisibility}
            hasRowDetails={!!renderRowDetails}
          />

          <TableBody>
            {loading && (
              <LoadingSkeleton
                columns={columns}
                rowCount={paginationToUse.pageSize}
                rowActions={rowActions}
                withCheckboxes={withCheckboxes}
                checkColumnVisibility={checkColumnVisibility}
              />
            )}

            {shouldDisplayNoData && (
              <NoDataPlaceholder colSpan={columnCount} message={noDataMessage} />
            )}

            {shouldDisplayData &&
              displayData.map((row) => (
                <Row
                  key={row.__id}
                  row={row}
                  columns={columns}
                  onRowClick={onRowClick}
                  rowActions={rowActions}
                  totalColumns={columnCount}
                  withCheckbox={withCheckboxes}
                  handleRowCheck={handleRowCheck}
                  renderRowDetails={renderRowDetails}
                  isSelected={!!selectedRows[row.__id]}
                  toggleRowDetails={handleToggleRowDetails}
                  isRowDetailsOpen={!!expandedRows[row.__id]}
                  checkColumnVisibility={checkColumnVisibility}
                  isRowCheckable={isRowCheckable?.(row) ?? true}
                  isRowClickable={isRowClickable?.(row) ?? !!onRowClick}
                  rowSx={contentRowSx}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {shouldDisplayPagination && (
        <Pagination pagination={paginationToUse} onPaginationChange={handlePaginationChange} />
      )}
    </Box>
  )
}
